import React, { useState, useEffect } from 'react';
import {Link} from 'react-router-dom';
import moment from 'moment';
import * as Styled from './style.js';
import { getData } from '../../config/api-connect';
import { Table, Spin, Skeleton, Empty} from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,PieChart,Pie,Cell } from 'recharts';
import {toProperCase} from '../../utils/stringUtil.js'

const COLORS = ['#04BFB9','#39b351','#4682b4','#bdb76b', '#FF8042'];

const LeadPipeline = ({ leadsPipelineRef, cities, venueId, month, year }) => {
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [chartLoading,setChartLoading] = useState(true)
  const [leadSummary, setLeadSummary] = useState([]);
  const [leadQualities, setLeadQualities] = useState([]);
  const [pieGraphData, setPieGraphData] = useState([])
  const [leadCount, setLeadCount] = useState('')

  useEffect(() => {
    getVenue();
    if (cities !== '') {
      getLeadsPipeline();
      getLeadPipelineGraph()
    }
  }, [cities, venueId]);

  const getCellStyle = (type, quality) => {
    if ((type === 'Response' && quality === 'Sure Payment') || (type === 'Contact Later' && quality === 'Highly Likely')) {
      return { background: '#E9F6EE', color: '#1b3d2f' };
    }
    if ((type === 'Contact Later' && quality === 'Sure Payment') || (type === 'Demo' && quality === 'Highly Likely')) {
      return { background: '#C9E8D2', color: '#1b3d2f' };
    }
    if ((type === 'Demo' && quality === 'Sure Payment') || (type === 'Verbal Confirmation' && quality === 'Highly Likely')) {
      return { background: '#A8DDBA', color: '#1b3d2f' };
    }
    if (type === 'Verbal Confirmation' && quality === 'Sure Payment') {
      return { background: 'rgba(0, 119, 51, 0.6)', color: '#fff' };
    }
    if(type === 'Response' && quality === 'Highly Likely') {
      return {background: '#F0F9F3',color: '#1b3d2f'}
    }
    return {};
  };

  const lead_columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      align: 'center',
    },
    ...leadQualities.map(quality => ({
      title: quality,
      dataIndex: quality,
      key: quality,
      align: 'center',
      render: (text,record) => {
        const qualityId = record[`${quality}_id`]; 
        return(
          <Link 
            to={`/lead-pipeline/details/?quality=${qualityId}&type=${record.key}`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <span>{text || 0}</span>
          </Link>
        )
      },
      onCell: (record) => ({
        style: getCellStyle(record.type, quality)
      })
    }))
  ];

  const getGraphData = async (venue, cities) => {
    setLoading(true);
    await getData(`/business/get_leads_graph/?venue_id=${venue}&city_id=${cities}&month=${month}&year=${year}&lead_type=leads_generated`)
      .then(response => setGraphData(response.result))
      .catch(error => console.log(error.message))
      .finally(() => setLoading(false));
  };

  const getVenue = async () => {
    const choosenVenue = venueId || 'All';
    const choosenCity = cities ? cities.join(',') : 'All';
    getGraphData(choosenVenue, choosenCity);
  };

  const getLeadsPipeline = async () => {
    setPageLoading(true);
    await getData(`/sales/get_lead_pipeline?venue_id=${venueId}&city_id=${cities}`)
      .then(response => {
        const { lead_qualities, results } = response;

        setLeadQualities(lead_qualities || []);

        const typeOrder = ['response', 'contact_later', 'demo', 'verbal_confirmation'];

        const transformedData = typeOrder
          .filter(type => results && results[type])
          .map(type => {
            const row = {
              type: toProperCase(type),
              key: type,
              rawType: type 
            };

            let total = 0;
            results[type].forEach(item => {
              row[item.name] = item.count;
              row[`${item.name}_id`] = item.id;
              total += item.count;
            });

            row.total = total;
            return row;
          });

        setLeadSummary(transformedData);
        setPageLoading(false);
      })
      .catch(error => {
        console.log(error);
        setPageLoading(false);
      });
  };

  const getLeadPipelineGraph = () => {
    setChartLoading(true)
    getData(`/business/get_hot_leads_graph/?venue_id=${venueId}&city_id=${cities}`)
    .then(response =>{
      setPieGraphData(response.result)
      setLeadCount(response.total)
    })
    .catch(error => {
      console.log(error.message);
    })
    .finally(()=>{
      setChartLoading(false)
    })
  }

  return (
    <div ref={leadsPipelineRef}>
      <Styled.Header>
        <span>Lead Pipeline</span>
      </Styled.Header>

      <Styled.PipelineGraphDiv>
        
        <Styled.GraphDivLeft>
          <Styled.GraphDivLabelDiv>
            <Styled.LabelHeading> Leads Generated - Analysis </Styled.LabelHeading>
          </Styled.GraphDivLabelDiv>

          {chartLoading ?
            <div style={{ height: '25vh',display: 'flex',alignItems: 'center',justifyContent: 'center' }}>
              <Spin />
            </div>
          :
            <ResponsiveContainer width="100%" height="80%">
              <LineChart
                width={500}
                height={300}
                data={graphData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month"
                  tickFormatter={(value) => moment(value, "MMMM YYYY").format("MMM")}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="rgb(30 124 10)" name="Leads Generated"/>  
              </LineChart>
            </ResponsiveContainer>
          }
        </Styled.GraphDivLeft>
        <span></span>

        <Styled.GraphDivRight style={{alignItem: 'center'}}>

          <Styled.GraphDivLabelDiv>
            <Styled.LabelHeading> Lead Pipeline </Styled.LabelHeading>
          </Styled.GraphDivLabelDiv>

          {
            leadCount > 0 ?              
              <Styled.PieDiv>
                            
                <div style={{position: 'relative'}}>
                  <PieChart width={200} height={200}>
                    <Pie
                      data={pieGraphData}
                      innerRadius={60}
                      fill="#8884d8"                        
                      dataKey="value"
                    >
                      {pieGraphData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                  <Styled.TotalAmount> {leadCount} </Styled.TotalAmount>
                </div>                              
            
                <Styled.PieDivInfo>
                  {
                    pieGraphData.sort((a,b)=> b.value - a.value).map((item,index)=>(
                      <Styled.InfoRow>
                        <Styled.InfoColor style={{backgroundColor: COLORS[index % COLORS.length]}} />
                        <Styled.InfoLabel> {item.name} - {item.value}</Styled.InfoLabel>
                      </Styled.InfoRow>
                    ))
                  }  
                </Styled.PieDivInfo>
            
              </Styled.PieDiv>
            :
             <Empty style={{marginTop: '10%'}} description = {<span style={{color: 'grey', fontSize: 'small'}}> data not available </span>} />
          }
        </Styled.GraphDivRight>

      </Styled.PipelineGraphDiv>

      {pageLoading ? (
        <Skeleton active paragraph={{ rows: 7 }} title={false} />
      ) : (
        <Table
          className="ant-table-thead table_container"
          rowKey="key"
          columns={lead_columns}
          dataSource={leadSummary}
          pagination={false}
          summary={() => {
            if (leadSummary.length === 0) return null;

            const totalRow = { type: 'Total' };
            let grandTotal = 0;

            leadQualities.forEach(quality => {
              const sum = leadSummary.reduce((acc, row) => acc + (row[quality] || 0), 0);
              totalRow[quality] = sum;
              grandTotal += sum;
            });

            totalRow.total = grandTotal;

            return (
              <Table.Summary.Row>
                {lead_columns.map(col => {
                  const key = col.dataIndex;
                  return (
                    <Table.Summary.Cell key={key} align={col.align}>
                      <b>{totalRow[key] !== undefined ? totalRow[key] : ''}</b>
                    </Table.Summary.Cell>
                  );
                })}
              </Table.Summary.Row>
            );
          }}
        />
      )}
    </div>
  );
};

export default LeadPipeline;
